import { useState, useEffect } from 'react';
import { Download, Package, Clock, CheckCircle, XCircle, Truck, Receipt } from 'lucide-react';
import api from '../../../api/axios';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import Skeleton from 'react-loading-skeleton';

export default function MedicineOrderHistory() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [filter, setFilter] = useState('');
  const [generatingInvoice, setGeneratingInvoice] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, [currentPage, filter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10
      });
      
      if (filter) {
        params.append('status', filter);
      }

      const response = await api.get(`/orders/history/?${params.toString()}`);
      
      if (response.data.success) {
        setOrders(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to load order history');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to load order history');
    } finally {
      setLoading(false);
    }
  };

  const generateInvoice = async (orderId) => {
    setGeneratingInvoice(orderId);
    try {
      const response = await api.post(`/orders/${orderId}/generate-invoice/`);
      
      if (response.data.success) {
        // Refresh the order to get the updated invoice URL
        fetchOrders();
        alert('Invoice generated successfully!');
      } else {
        alert('Failed to generate invoice');
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert('Failed to generate invoice');
    } finally {
      setGeneratingInvoice(null);
    }
  };

  const downloadInvoice = (invoiceUrl) => {
    window.open(`https://www.homecureexpress.com${invoiceUrl}`, '_blank');
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'shipped':
        return <Truck className="w-5 h-5 text-blue-500" />;
      default:
        return <Clock className="w-5 h-5 text-orange-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-orange-100 text-orange-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Medicine Order History
              </h1>
              <p className="text-gray-600 mt-1">
                Track and manage your medicine orders
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex flex-wrap gap-4 items-center">
              <label className="text-sm font-medium text-gray-700">Filter by Status:</label>
              <select
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Orders</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="space-y-6">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="space-y-2">
                      <Skeleton height={24} width={200} />
                      <Skeleton height={16} width={150} />
                    </div>
                    <Skeleton height={32} width={80} />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <Skeleton height={16} />
                    <Skeleton height={16} />
                    <Skeleton height={16} />
                    <Skeleton height={16} />
                  </div>
                  <Skeleton height={40} />
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && orders.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No orders found
              </h3>
              <p className="text-gray-500">
                {filter ? `No orders with status "${filter}"` : 'You haven\'t placed any medicine orders yet'}
              </p>
            </div>
          )}

          {/* Orders List */}
          {!loading && orders.length > 0 && (
            <div className="space-y-6">
              {orders.map((order) => (
                <div key={order.id} className="bg-white rounded-lg shadow-sm p-6">
                  {/* Order Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Order #{order.order_id}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Placed on {formatDate(order.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(order.status)}
                      <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </div>
                  </div>

                  {/* Order Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-500">Total Amount</p>
                      <p className="font-semibold">{formatCurrency(order.total_amount)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Delivery Charge</p>
                      <p className="font-semibold">{formatCurrency(order.delivery_charge)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Payment Status</p>
                      <p className={`font-semibold capitalize ${
                        order.payment_status === 'paid' ? 'text-green-600' : 'text-orange-600'
                      }`}>
                        {order.payment_status}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Last Updated</p>
                      <p className="font-semibold">{formatDate(order.updated_at)}</p>
                    </div>
                  </div>

                  {/* Medicines */}
                  {order.medicines_details && order.medicines_details.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Medicines:</h4>
                      <div className="flex flex-wrap gap-2">
                        {order.medicines_details.map((medicine, index) => (
                          <div key={index} className="flex items-center space-x-2 bg-gray-50 px-3 py-1 rounded-lg">
                            {medicine.image && (
                              <img 
                                src={`https://www.homecureexpress.com${medicine.image}`}
                                alt={medicine.name}
                                className="w-6 h-6 object-cover rounded"
                              />
                            )}
                            <span className="text-sm">{medicine.name}</span>
                            <span className="text-sm text-gray-500">
                              {formatCurrency(medicine.offer_price)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-4">
                      {order.invoice_url ? (
                        <button
                          onClick={() => downloadInvoice(order.invoice_url)}
                          className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download Invoice</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => generateInvoice(order.id)}
                          disabled={generatingInvoice === order.id}
                          className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 text-sm font-medium disabled:opacity-50"
                        >
                          <Receipt className="w-4 h-4" />
                          <span>
                            {generatingInvoice === order.id ? 'Generating...' : 'Generate Invoice'}
                          </span>
                        </button>
                      )}
                    </div>
                    
                    {order.invoice_number && (
                      <span className="text-xs text-gray-500">
                        Invoice: {order.invoice_number}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && pagination && pagination.total_count > pagination.limit && (
            <div className="flex justify-center items-center space-x-4 mt-8">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={!pagination.has_previous}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <span className="text-sm text-gray-700">
                Page {pagination.current_page || pagination.page} of {Math.ceil(pagination.total_count / pagination.limit)}
              </span>
              
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={!pagination.has_next}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}